/* eslint-disable camelcase */

import React from 'react';
import { ConfigService, withInject } from '@piwikpro/platform';
import {
  Button, ContactForm, Message, Skeleton, Stack, StyledContainer, Text, Tooltip, useTranslation,
} from '@piwikpro/ui-components';
import { Notifications } from '@piwikpro/notification-crate';
import { LicenseAttributes } from '@piwikpro/organization-crate/redux/details';
import { BillingCycle, ScheduledChangeAction, SubscriptionStatus } from '@piwikpro/marketing-crate/redux';

import { SubscriptionBadge } from './components/SubscriptionBadge/SubscriptionBadge';
import { LicenseDateBadge } from './components/LicenseDateBadge/LicenseDateBadge';
import styles from './LicenseDetailsHeader.module.css';

const utcIsoDateMinusOneDay = (isoDate: string): string => {
  const date = new Date(isoDate);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString();
};

type LicenseDetailsHeaderProps = {
  mode: 'administration' | 'support-panel'
  loading: boolean
  error: boolean
  license: LicenseAttributes | null
  upcomingLicense?: LicenseAttributes | null
  subscription: null | {
    status: SubscriptionStatus
    next_payment: string | null
    billing_cycle: BillingCycle | null
    scheduled_change_action: ScheduledChangeAction
    scheduled_change_effective_at: string | null
  }
  onAddLicense?: () => void
  onEditLicense?: () => void
  config: ConfigService
  notifications: Notifications
};

const LicenseDetailsHeader = ({
  mode,
  loading,
  error,
  license,
  upcomingLicense,
  subscription,
  onAddLicense,
  onEditLicense,
  config,
  notifications,
}: LicenseDetailsHeaderProps) => {
  const { t } = useTranslation();

  if (error) {
    return (
      <StyledContainer padding="mid">
        <Message messageType="error" message={t('general-errors.inline-error.text')} centerHorizontal />
      </StyledContainer>
    );
  }

  const hadSubscription = !subscription && license?.expires_at === null;

  // This is a fake subscription object that is used to display the subscription badge when the subscription is cancelled.
  // After scheduling a new license, the subscription is cancelled immediately.
  // We need to display the subscription badge to indicate that the subscription will be active until the end of current billing cycle.

  const fakeSubscription = {
    status: 'active' as const,
    next_payment: null,
    billing_cycle: null,
    scheduled_change_action: 'cancel' as const,
    scheduled_change_effective_at: upcomingLicense?.active_at
      ? utcIsoDateMinusOneDay(upcomingLicense.active_at)
      : null,
  };

  const subscriptionToDisplay = hadSubscription && upcomingLicense ? fakeSubscription : subscription;
  const hasScheduledPaymentUpdate = subscription?.scheduled_change_action === 'update';
  let addLicenseDisabledTooltip: string | undefined;
  if (upcomingLicense) {
    addLicenseDisabledTooltip = hasScheduledPaymentUpdate
      ? 'Only one active and one scheduled license are allowed.'
      : 'Only one active and one scheduled license are allowed. Cancel the scheduled license to add a new one.';
  }
  const isEditLicenseDisabled = loading || subscription?.status === 'past_due';
  const editLicenseTooltipContent = subscription?.status === 'past_due'
    ? 'License editing is disabled because the last payment failed.'
    : undefined;

  return (
    <div className={styles.wrapper}>
      <StyledContainer backgroundImage="image3" padding="wide" border={[]}>
        <Stack verticalAlignment="center">
          <Stack.Item fill>
            <Stack vertical spacing="narrow">
              <Stack.Item>
                <div className={styles.caption}>
                  <>
                    {mode === 'administration' && t('platform.license.plan.your-plan')}
                    {mode === 'support-panel' && t('platform.license.plan.title')}
                  </>
                </div>
              </Stack.Item>
              <Stack.Item fill>
                <Stack verticalAlignment="center" spacing="narrow">
                  <Stack.Item>
                    <Text
                      as={mode === 'support-panel' ? 'h3' : 'h2'}
                      size={mode === 'support-panel' ? 16 : 20}
                      weight={700}
                      wrapType="noWrapAndCutText"
                      dataId={mode === 'support-panel' ? 'Header.small' : 'Header.medium'}
                    >
                      {loading
                        ? <Skeleton width={100} height={24} />
                        : license?.plan.name}
                    </Text>
                  </Stack.Item>
                  <Stack.Item fill>
                    {loading
                      ? <Skeleton width={100} height={16} />
                      : (
                        <>
                          {subscriptionToDisplay
                            ? <SubscriptionBadge subscription={subscriptionToDisplay} mode={mode} />
                            : <LicenseDateBadge license={license} upcomingLicense={upcomingLicense} mode={mode} />}
                        </>
                      )}
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
          <Stack.Item>
            {mode === 'support-panel' && (
              <Stack spacing="narrow">
                <Tooltip content={editLicenseTooltipContent}>
                  <Button
                    text="Edit a license"
                    disabled={isEditLicenseDisabled}
                    onClick={onEditLicense}
                  />
                </Tooltip>
                <Tooltip content={addLicenseDisabledTooltip}>
                  <Button
                    disabled={!!upcomingLicense}
                    appearance="primary"
                    text="Add a license"
                    onClick={onAddLicense}
                  />
                </Tooltip>
              </Stack>
            )}
            {mode === 'administration' && (
              <>
                {loading
                  ? <Skeleton width={82} height={32} />
                  : (
                    <>
                      {((license?.plan.id === 'trial') || (subscription !== null)) ? (
                        <Button
                          appearance="primary"
                          icon="rocket"
                          text={t('platform.license.upgrade.button')}
                          onClick={() => {
                            window.open(config.get('PAYMENT_BASE_URL'));
                          }}
                        />
                      ) : (
                        <ContactForm
                          type="account"
                          onSubmitSuccess={({ message }) => {
                            notifications.push({ text: message, type: 'success', autoCloseSeconds: 5 });
                          }}
                          onSubmitFail={({ message }) => {
                            notifications.push({ text: message, type: 'error', autoCloseSeconds: 5 });
                          }}
                        >
                          {({ open }) => (
                            <Button text={t('platform.license.contact-us.button')} onClick={open} />
                          )}
                        </ContactForm>
                      )}
                    </>
                  )
                }
              </>
            )}
          </Stack.Item>
        </Stack>
      </StyledContainer>
    </div>
  );
};

const InjectedLicenseDetailsHeader = withInject<Pick<LicenseDetailsHeaderProps, 'config' | 'notifications'>>({
  config: 'config',
  notifications: 'NotificationCrate.notifications',
})(LicenseDetailsHeader);

export { LicenseDetailsHeader as LicenseDetailsHeaderBase };
export { InjectedLicenseDetailsHeader as LicenseDetailsHeader };
